import { useContext, useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ToastContext, ToastContextType } from "../utilities/customHooks";
import { FileSignature, Loader2, CheckCircle, AlertCircle, Eraser, PenLine, Type, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../lib/utils";
import type { InPortalSignerInfo } from "@eip/shared/types/sow.types";

type Props = {
  sowId: string;
  companyName: string;
  tier: string;
  getSigningInfo: (sowId: string) => Promise<InPortalSignerInfo>;
  submitSignature: (sowId: string, signatureBase64: string, consent: boolean, printedName: string) => Promise<{ success: boolean }>;
  onSigningComplete?: () => void;
};

type SignatureMode = "draw" | "type";
type SigningStep = 1 | 2 | 3;

const STEP_LABELS = ["Review", "Sign", "Confirm"] as const;

// Fonts available for typed signatures
const SIGNATURE_FONTS = [
  { name: "Cursive", style: "'Dancing Script', cursive" },
  { name: "Handwritten", style: "'Caveat', cursive" },
  { name: "Formal", style: "'Great Vibes', cursive" },
];

// Google Fonts link for typed signature fonts
const FONT_LINK_ID = "eip-signature-fonts";

function ensureSignatureFonts() {
  if (document.getElementById(FONT_LINK_ID)) return;
  const link = document.createElement("link");
  link.id = FONT_LINK_ID;
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Caveat:wght@700&family=Great+Vibes&display=swap";
  document.head.appendChild(link);
}

/**
 * Convert typed text to a PNG signature image using a canvas.
 */
function textToSignatureBase64(text: string, fontFamily: string): string {
  const canvas = document.createElement("canvas");
  canvas.width = 700;
  canvas.height = 150;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = `48px ${fontFamily}`;
  ctx.fillStyle = "black";
  ctx.textBaseline = "middle";
  ctx.fillText(text, 20, 80);
  return canvas.toDataURL("image/png").replace(/^data:image\/png;base64,/, "");
}

/**
 * A dashboard card that prompts the user to sign a SoW.
 * Opens an in-portal signing dialog with a guided 3-step flow:
 * 1. Review Document — PDF viewer + signer status
 * 2. Sign — Signature capture (draw/type)
 * 3. Confirm — Preview signature + consent checkbox + submit
 */
export default function SowSigningCard({ sowId, companyName, tier, getSigningInfo, submitSignature, onSigningComplete }: Props) {
  const { showToast } = useContext(ToastContext) as ToastContextType;
  const [signingOpen, setSigningOpen] = useState(false);
  const [signingInfo, setSigningInfo] = useState<InPortalSignerInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [initialChecking, setInitialChecking] = useState(true);
  const [signed, setSigned] = useState(false);
  const [consent, setConsent] = useState(false);
  const [mode, setMode] = useState<SignatureMode>("draw");
  const [typedName, setTypedName] = useState("");
  const [selectedFont, setSelectedFont] = useState(0);
  const [step, setStep] = useState<SigningStep>(1);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [printedName, setPrintedName] = useState("");
  const sigPadRef = useRef<SignatureCanvas | null>(null);

  // Load Google Fonts for typed signatures
  useEffect(() => {
    ensureSignatureFonts();
  }, []);

  // Check if already signed on mount (prevents amber → green flash on page refresh)
  useEffect(() => {
    let cancelled = false;
    getSigningInfo(sowId)
      .then((info) => {
        if (cancelled) return;
        if (info.signerStatus === "signed") setSigned(true);
        setSigningInfo(info);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setInitialChecking(false); });
    return () => { cancelled = true; };
  }, [sowId]);

  const handleOpenSigning = async () => {
    setLoading(true);
    try {
      // Re-fetch fresh status (may have changed since mount)
      const info = await getSigningInfo(sowId);
      if (info.signerStatus === "signed") {
        setSigned(true);
        return;
      }
      setSigningInfo(info);
      setSigningOpen(true);
      setConsent(false);
      setMode("draw");
      setTypedName("");
      setPrintedName("");
      setStep(1);
      setSignaturePreview(null);
    } catch {
      showToast({ toastType: "failure", message: "Unable to load signing interface. Please try again.", autoDisappearTimeInSeconds: 5 });
    } finally {
      setLoading(false);
    }
  };

  const getSignatureBase64 = (): string | null => {
    if (mode === "draw") {
      if (!sigPadRef.current || sigPadRef.current.isEmpty()) return null;
      return sigPadRef.current.toDataURL("image/png").replace(/^data:image\/png;base64,/, "");
    } else {
      if (!typedName.trim()) return null;
      return textToSignatureBase64(typedName.trim(), SIGNATURE_FONTS[selectedFont].style);
    }
  };

  const handleAdvanceToConfirm = () => {
    if (!printedName.trim()) {
      showToast({
        toastType: "failure",
        message: "Please enter your full legal name before continuing.",
        autoDisappearTimeInSeconds: 5,
      });
      return;
    }
    const base64 = getSignatureBase64();
    if (!base64) {
      showToast({
        toastType: "failure",
        message: mode === "draw" ? "Please draw your signature before continuing." : "Please type your name to sign.",
        autoDisappearTimeInSeconds: 5,
      });
      return;
    }
    setSignaturePreview(base64);
    setStep(3);
  };

  const handleSubmit = async () => {
    if (!signaturePreview) return;
    if (!consent) {
      showToast({ toastType: "failure", message: "Please check the consent box to proceed.", autoDisappearTimeInSeconds: 5 });
      return;
    }

    setSubmitting(true);
    try {
      await submitSignature(sowId, signaturePreview, true, printedName.trim());
      setSigned(true);
      showToast({ toastType: "success", message: "SoW signed successfully!", autoDisappearTimeInSeconds: 5 });
      setSigningOpen(false);
      onSigningComplete?.();
    } catch {
      showToast({ toastType: "failure", message: "Failed to submit signature. Please try again.", autoDisappearTimeInSeconds: 5 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = () => {
    if (mode === "draw") {
      sigPadRef.current?.clear();
    } else {
      setTypedName("");
    }
  };

  if (initialChecking) {
    return (
      <Card className="border-border">
        <CardContent className="p-6">
          <div className="h-6 w-48 bg-muted rounded animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  if (signed) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <div>
              <p className="font-medium text-green-900">SoW Signed</p>
              <p className="text-sm text-green-700">Your signature has been recorded. The other party will be notified.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <FileSignature className="h-6 w-6 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">Signature Required</p>
                <p className="text-sm text-amber-700 mt-1">
                  The Tier {tier} SoW for {companyName} is ready for your signature.
                  Please review and sign so work can begin.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={handleOpenSigning}
              disabled={loading}
              className="shrink-0"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  Loading...
                </>
              ) : (
                "Review & Sign"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={signingOpen} onOpenChange={(open) => { if (!open) setSigningOpen(false); }}>
        <DialogContent className="w-[95vw] max-w-[95vw] h-[95vh] max-h-[95vh] p-0 flex flex-col">
          <DialogHeader className="px-4 pt-4 pb-2 shrink-0">
            <DialogTitle>Sign Scope of Work — {companyName}</DialogTitle>
          </DialogHeader>

          {/* Main body: vertical stepper rail + content */}
          <div className="flex flex-1 min-h-0">
            {/* Vertical step indicator — left rail */}
            <div className="shrink-0 flex flex-col items-center gap-0 px-4 py-2 border-r">
              {STEP_LABELS.map((label, i) => {
                const stepNum = (i + 1) as SigningStep;
                const isCompleted = step > stepNum;
                const isActive = step >= stepNum;
                return (
                  <div key={label} className="flex flex-col items-center">
                    <div className={cn(
                      "h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors",
                      isActive
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background text-muted-foreground"
                    )}>
                      {isCompleted ? <CheckCircle className="h-4 w-4" /> : stepNum}
                    </div>
                    <span className={cn("text-xs mt-1 mb-1", isActive ? "text-foreground font-medium" : "text-muted-foreground")}>{label}</span>
                    {i < STEP_LABELS.length - 1 && (
                      <div className={cn("w-0.5 h-6 transition-colors", step > stepNum ? "bg-primary" : "bg-border")} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Step content */}
            <div className="flex-1 min-w-0 flex flex-col min-h-0">
              {/* ─── Step 1: Review Document ─── */}
              {step === 1 && (
                <div className="flex-1 overflow-auto px-4 min-h-0 flex flex-col">
                  {/* PDF Viewer */}
                  {signingInfo?.sowPdfUrl ? (
                    <iframe
                      src={signingInfo.sowPdfUrl}
                      title="SoW PDF"
                      className="w-full flex-1 rounded-md border border-border"
                      style={{ minHeight: "400px" }}
                    />
                  ) : (
                    <div className="w-full flex-1 flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </div>
              )}

          {/* ─── Step 2: Sign ─── */}
          {step === 2 && (
            <div className="flex-1 overflow-auto p-4 pt-2 min-h-0">
              <div className="max-w-2xl mx-auto space-y-4">
                <p className="text-sm text-muted-foreground">
                  Enter your full legal name, then create your signature below.
                </p>

                {/* Full legal name input */}
                <div>
                  <label className="text-sm font-medium mb-1.5 block">Full Legal Name</label>
                  <input
                    type="text"
                    value={printedName}
                    onChange={(e) => setPrintedName(e.target.value)}
                    placeholder="Enter your full legal name as it should appear on the document"
                    className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>

                {/* Mode toggle + clear */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant={mode === "draw" ? "default" : "ghost"}
                      className="h-7 px-2 text-xs"
                      onClick={() => setMode("draw")}
                    >
                      <PenLine className="h-3 w-3 mr-1" />
                      Draw
                    </Button>
                    <Button
                      size="sm"
                      variant={mode === "type" ? "default" : "ghost"}
                      className="h-7 px-2 text-xs"
                      onClick={() => setMode("type")}
                    >
                      <Type className="h-3 w-3 mr-1" />
                      Type
                    </Button>
                  </div>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={handleClear}>
                    <Eraser className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                </div>

                {mode === "draw" ? (
                  <div className="border-2 border-dashed border-border rounded-md bg-white touch-none">
                    <SignatureCanvas
                      ref={sigPadRef}
                      canvasProps={{
                        width: 700,
                        height: 150,
                        className: "w-full rounded-md",
                        style: { width: "100%", height: "150px" },
                      }}
                      penColor="black"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={typedName}
                      onChange={(e) => setTypedName(e.target.value)}
                      placeholder="Type your full name"
                      className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    {typedName.trim() && (
                      <div className="border-2 border-dashed border-border rounded-md bg-white p-4 flex items-center justify-center min-h-[100px]">
                        <span
                          style={{ fontFamily: SIGNATURE_FONTS[selectedFont].style, fontSize: "48px" }}
                          className="text-black select-none"
                        >
                          {typedName}
                        </span>
                      </div>
                    )}
                    <div className="flex gap-2">
                      {SIGNATURE_FONTS.map((font, i) => (
                        <button
                          key={font.name}
                          onClick={() => setSelectedFont(i)}
                          className={`px-3 py-1 text-xs rounded border ${selectedFont === i ? "border-primary bg-primary/10 font-medium" : "border-border hover:border-primary/50"}`}
                        >
                          {font.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Step 3: Confirm & Submit ─── */}
          {step === 3 && (
            <div className="flex-1 overflow-auto p-4 pt-2 min-h-0">
              <div className="max-w-2xl mx-auto space-y-5">
                <p className="text-sm text-muted-foreground">
                  Review your signature below. Once submitted, this is legally binding.
                </p>

                {/* Summary */}
                <div className="rounded-md border bg-muted/30 p-4 space-y-2 text-sm">
                  <div><span className="text-muted-foreground">Company:</span> <strong>{companyName}</strong></div>
                  <div><span className="text-muted-foreground">SoW Tier:</span> <strong>{tier}</strong></div>
                  <div><span className="text-muted-foreground">Signing as:</span> <strong>{signingInfo?.signerRole === "founder" ? "Participant Representative" : "Service Provider"}</strong></div>
                  <div><span className="text-muted-foreground">Print Name:</span> <strong>{printedName}</strong></div>
                </div>

                {/* Signature preview */}
                <div>
                  <p className="text-sm font-medium mb-2">Your Signature</p>
                  <div className="border-2 border-dashed border-border rounded-md bg-white p-4 flex items-center justify-center min-h-[80px]">
                    {signaturePreview && (
                      <img
                        src={`data:image/png;base64,${signaturePreview}`}
                        alt="Your signature"
                        className="max-h-[60px] max-w-full"
                      />
                    )}
                  </div>
                </div>

                {/* Consent checkbox */}
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm text-muted-foreground leading-snug">
                    I agree to sign this Scope of Work document electronically. I understand this constitutes a legally binding signature under Canadian law.
                  </span>
                </label>

              </div>
            </div>
          )}

            </div>{/* end step content */}
          </div>{/* end flex row (stepper + content) */}

          {/* ─── Footer ─── */}
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-t shrink-0">
            {/* Left side: signer status + back button */}
            <div className="flex items-center gap-4">
              {step > 1 && (
                <Button variant="secondary" onClick={() => setStep((step - 1) as SigningStep)} disabled={submitting}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              )}
              <div className="flex gap-3 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  {signingInfo?.signerStatus === "signed"
                    ? <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                    : <AlertCircle className="h-3.5 w-3.5 text-amber-500" />}
                  <span>{signingInfo?.signerName || "You"}: <strong className="text-foreground">{signingInfo?.signerStatus === "signed" ? "Signed" : "Pending"}</strong></span>
                </div>
                <div className="flex items-center gap-1">
                  {signingInfo?.otherSignerStatus === "signed"
                    ? <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                    : <AlertCircle className="h-3.5 w-3.5 text-amber-500" />}
                  <span>{signingInfo?.otherSignerName || "Other party"}: <strong className="text-foreground">{signingInfo?.otherSignerStatus === "signed" ? "Signed" : "Pending"}</strong></span>
                </div>
              </div>
            </div>
            {/* Right side: action buttons */}
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setSigningOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              {step === 1 && (
                <Button onClick={() => setStep(2)}>
                  Continue to Sign
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
              {step === 2 && (
                <Button onClick={handleAdvanceToConfirm}>
                  Review &amp; Confirm
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
              {step === 3 && (
                <Button onClick={handleSubmit} disabled={submitting || !consent}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Signature"
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
